/** 与 web `appBridge` 桥接参数对齐（App 跳转购买时透传） */
import { EXPIRED_PURCHASE_PATH } from 'src/components/expired-purchase-guide/constants'
import { loadUserEntryFlags, resolveUserEntry, USER_ENTRY } from 'src/utils/userEntryGate'

const BRIDGE_QUERY_KEYS = [
  'from_app',
  'app_platform',
  'anonymous_user_id',
  'app_session_id',
  'web_bridge_id',
  'source_screen',
  'cta_name',
  'feature_id',
  'feature_name',
  'installid',
  'idfa',
  'ver',
  'agent'
]

const WEB_SUPPORTED_LOCALES = new Set(['en', 'de', 'es', 'fr', 'it', 'pt', 'jp', 'kr', 'ar', 'tr', 'tw'])

const getWebLocale = () => {
  const dashboardLocale = localStorage.getItem('locale') || 'en'
  const webLocale = dashboardLocale === 'it-ch' ? 'it' : dashboardLocale

  return WEB_SUPPORTED_LOCALES.has(webLocale) ? webLocale : 'en'
}

/** 未过期、可绑定的未绑单（与跳转选购页判定一致） */
export const filterValidUnboundOrders = list => {
  const orders = Array.isArray(list) ? list : []
  const now = Date.now()

  return orders.filter(item => {
    const expireTime = item?.expire_time ? new Date(item.expire_time).getTime() : 0

    return Number.isFinite(expireTime) && expireTime >= now
  })
}

/** 无有效未绑定订单（空列表或全部过期） */
export const shouldRedirectToBuyPackage = list => filterValidUnboundOrders(list).length === 0

const isFromAppClient = () => {
  if (typeof window === 'undefined') return false

  try {
    if (new URLSearchParams(window.location.search).get('from_app') === '1') return true
    if (localStorage.getItem('installid')) return true

    const bridge = JSON.parse(sessionStorage.getItem('spyx_web_app_bridge') || 'null')
    if (bridge?.from_app === '1') return true
  } catch (e) {
    // ignore
  }

  return false
}

const buildClientBuyPath = () => {
  const token = localStorage.getItem('accessToken')
  const fromApp = isFromAppClient()
  const params = new URLSearchParams()

  if (token) params.set('token', token)

  // App 直达选套餐，跳过 Step2 选设备
  if (fromApp) {
    params.set('from_app', '1')

    try {
      const current = new URLSearchParams(window.location.search)
      BRIDGE_QUERY_KEYS.forEach(key => {
        const value = current.get(key)
        if (value) params.set(key, value)
      })
    } catch (e) {
      // ignore
    }

    params.set('locale', getWebLocale())

    const qs = params.toString()

    return `/step/pricing${qs ? `?${qs}` : ''}`
  }

  params.set('locale', getWebLocale())

  return `/step/choose-device?${params.toString()}`
}

/**
 * 去购买：先经 Web 活动入口解析；没有有效活动时回到原购买路径。
 * App 直达选套餐，普通 Web 仍先选设备，原有桥接和归因参数继续透传。
 */
export const redirectToChooseDevice = () => {
  const clientUrl = (process.env.NEXT_PUBLIC_CLIENT_URL || '').replace(/\/+$/, '')
  const fallbackPath = buildClientBuyPath()
  const [pathname, search = ''] = fallbackPath.split('?')
  const params = new URLSearchParams(search)
  params.set('returnTo', pathname)

  const purchaseEntry = `${clientUrl}/purchase-entry?${params.toString()}`
  window.location.assign(clientUrl ? purchaseEntry : fallbackPath)
}

/**
 * Guide 顶栏「Add New Device」：
 * 已有套餐（已绑设备或有效未绑单）→ Dashboard；否则 → choose-device
 */
export const redirectGuideAddNewDevice = async () => {
  try {
    const { getAllUserDevice, getNotBindOrder } = await import('src/@api/login')
    const [devicesRes, ordersRes] = await Promise.all([
      getAllUserDevice({}).catch(() => ({ data: [] })),
      getNotBindOrder({}).catch(() => ({ data: [] }))
    ])

    const devices = Array.isArray(devicesRes?.data) ? devicesRes.data : []
    const hasValidUnboundOrders = filterValidUnboundOrders(ordersRes?.data).length > 0

    if (devices.length > 0 || hasValidUnboundOrders) {
      window.location.assign('/dashboard')

      return
    }
  } catch (error) {
    console.error('[redirectGuideAddNewDevice]', error)
  }

  redirectToChooseDevice()
}

/**
 * setup 类页面的兜底：没有有效未绑单时离开绑定流程。
 * 优先级由 userEntryGate 统一裁决：全部订单过期超 30 天 → 过期页，其余 → 去购买。
 * 已有有效未绑单则原地留在绑定流程。
 * @returns {Promise<boolean>} 是否已发生跳转
 */
export const redirectIfShouldBuyPackage = async orderResult => {
  const list = orderResult?.data || []
  const validUnbound = filterValidUnboundOrders(list)

  if (validUnbound.length > 0) return false

  const flags = await loadUserEntryFlags()
  const entryType = resolveUserEntry({ flags, validUnboundOrders: validUnbound })

  if (entryType === USER_ENTRY.EXPIRED) {
    window.location.assign(EXPIRED_PURCHASE_PATH)

    return true
  }

  redirectToChooseDevice()

  return true
}
